/** 路由表。家长端 / 学生端 / 系统管理后台分开挂载，并用 RequireAuth 限制角色。 */
import { Navigate, Link, Route, Routes } from 'react-router-dom';
import { RequireAuth, RequireGuest, RequireSystemAdmin } from './components/RouteGuards';
import { EmptyState } from './components/ui/Card';
import { AppShell } from './components/layout/AppShell';
import { routePath } from './routes';
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage';
import { ParentHomePage } from './pages/parent/ParentHomePage';
import { StudentDetailPage } from './pages/parent/StudentDetailPage';
import { ParentStudentPointsPage } from './pages/parent/ParentStudentPointsPage';
import { ParentNodesPage } from './pages/parent/ParentNodesPage';
import { ParentHomeworkPage } from './pages/parent/ParentHomeworkPage';
import { ParentDictationPage } from './pages/parent/ParentDictationPage';
import { ParentStudentDictationPage } from './pages/parent/ParentStudentDictationPage';
import { ParentDictationSessionPage } from './pages/parent/ParentDictationSessionPage';
import { ParentDictationPreviewPage } from './pages/parent/ParentDictationPreviewPage';
import { ParentDictationReviewPage } from './pages/parent/ParentDictationReviewPage';
import { ParentDictationMistakesPage } from './pages/parent/ParentDictationMistakesPage';
import { ParentRewardsPage } from './pages/parent/ParentRewardsPage';
import { ParentRedemptionsPage } from './pages/parent/ParentRedemptionsPage';
import { ParentStatsPage } from './pages/parent/ParentStatsPage';
import { ToolsPage } from './pages/parent/ToolsPage';
import { SettingsPage } from './pages/parent/SettingsPage';
import { StudentTodayPage } from './pages/student/StudentTodayPage';
import { StudentHomeworkPage } from './pages/student/StudentHomeworkPage';
import { StudentDictationPage } from './pages/student/StudentDictationPage';
import { StudentDictationSessionPage } from './pages/student/StudentDictationSessionPage';
import { StudentDictationPreviewPage } from './pages/student/StudentDictationPreviewPage';
import { StudentPointsPage } from './pages/student/StudentPointsPage';
import { StudentRewardsPage } from './pages/student/StudentRewardsPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminOverviewPage } from './pages/admin/AdminOverviewPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AdminDictationPage } from './pages/admin/AdminDictationPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { AdminAuditPage } from './pages/admin/AdminAuditPage';
import { AdminMailTestPage } from './pages/admin/AdminMailTestPage';

function NotFoundPage() {
  return (
    <AppShell kind="parent" title="页面不存在">
      <EmptyState
        icon="🧭"
        title="没有找到这个页面"
        description="可能链接输错了，或者这个页面已经调整位置。"
        action={
          <Link to="/" className="mt-2 text-sm font-medium text-brand-600 hover:underline">
            回到首页
          </Link>
        }
      />
    </AppShell>
  );
}

export function App() {
  return (
    <Routes>
      {/* ---------- 未登录可见 ---------- */}
      <Route
        path={routePath('/login')}
        element={
          <RequireGuest>
            <LoginPage />
          </RequireGuest>
        }
      />
      <Route
        path={routePath('/register')}
        element={
          <RequireGuest>
            <RegisterPage />
          </RequireGuest>
        }
      />
      {/* 邮箱验证页要允许"已注册但未验证"的状态进入，因此不套 RequireGuest */}
      <Route path={routePath('/register/verify')} element={<VerifyEmailPage />} />
      <Route
        path={routePath('/forgot')}
        element={
          <RequireGuest>
            <ForgotPasswordPage />
          </RequireGuest>
        }
      />
      <Route
        path={routePath('/reset')}
        element={
          <RequireGuest>
            <ResetPasswordPage />
          </RequireGuest>
        }
      />

      {/* ---------- 家长端 ---------- */}
      <Route
        path={routePath('/')}
        element={
          <RequireAuth role="parent">
            <ParentHomePage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id')}
        element={
          <RequireAuth role="parent">
            <StudentDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id/points')}
        element={
          <RequireAuth role="parent">
            <ParentStudentPointsPage />
          </RequireAuth>
        }
      />
      {/* ---------- 家长端：目标 · 打卡任务 · 奖品 · 兑换审批 · 统计 ---------- */}
      <Route
        path={routePath('/students/:id/nodes')}
        element={
          <RequireAuth role="parent">
            <ParentNodesPage />
          </RequireAuth>
        }
      />
      {/* 2026-10-31：「打卡任务总览」页已删除（用户要求）。它的两块内容
          （调整打卡任务顺序 / 看孩子看到的清单）挪进了「目标」页底部。 */}
      {/* 学校作业：按天录入 / 复制上次结构 / 查过往。与 nodes·habits 同级同形，
          入口在孩子详情页的按钮行里（见 StudentDetailPage）。 */}
      <Route
        path={routePath('/students/:id/homework')}
        element={
          <RequireAuth role="parent">
            <ParentHomeworkPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id/rewards')}
        element={
          <RequireAuth role="parent">
            <ParentRewardsPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id/redemptions')}
        element={
          <RequireAuth role="parent">
            <ParentRedemptionsPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id/stats')}
        element={
          <RequireAuth role="parent">
            <ParentStatsPage />
          </RequireAuth>
        }
      />
      {/*
        ---------- 「工具」（2026-10-14 导航重构） ----------
        原来这里是一条 `/shares`（共享管理）路由。用户的原话是
        "「共享」没必要放到那里，因为共享是针对每个孩子的进行共享的"，
        于是顶层导航的这一格改成「工具」，共享的**收件箱**（别人把孩子共享给我、
        待我同意/拒绝）搬进 `/tools`，而"针对某个孩子的共享"本来就在孩子详情页里。

        ⚠️ 收件箱**不能因为改导航就消失**：它在 `/tools` 页上原样保留
        （同意 / 拒绝 / 待我处理的数量角标，见 `ToolsPage`）。
      */}
      <Route
        path={routePath('/tools')}
        element={
          <RequireAuth role="parent">
            <ToolsPage />
          </RequireAuth>
        }
      />
      {/*
        ---------- 家长端「工具」里的语文听写：**只用，不改** ----------
        ⚠️ 2026-10-16 起这一页是**只读 + 取词**：字表（课本/课文/生字/词语/
        收录关系）是**站点资产**，维护归系统管理员（`/admin/dictation`，
        见下面的 AdminDictationPage）。用户原话："……不是家长负责维护，
        而应该是站点负责维护……录入维护这一块应该由系统管理员去做。"

        页面上**不渲染任何**编辑/导入入口 —— 不是禁用按钮，而是那些端点
        对家长根本不存在（后端 21 条维护端点已搬到 `/admin/dictation/*`）。

        ⚠️ 家长自己要做的"给多音字录一段音"**不在这里**：录音是**家庭专属**的，
        挂在孩子那一页（`/students/:id/dictation` 的「朗读录音」）。
      */}
      <Route
        path={routePath('/tools/dictation')}
        element={
          <RequireAuth role="parent">
            <ParentDictationPage />
          </RequireAuth>
        }
      />
      {/*
        语文听写（第三步：听写设置 / 布置听写 / 做听写）—— **挂在孩子下面**。

        ⚠️ 它与上面那条顶层工具页**不是同一件事**（用户明确要求过）：
          · `/tools/dictation`        —— 数据录入（字表），全家共用；
          · `/students/:id/dictation` —— 这个孩子的听写设置 / 布置 / 历史 / 录音。
        数据归属不同（会话表有 `student_id`），所以路径也必须不同 ——
        共用一条路径会让"给孩子布置听写"变成"打开字表录入页"。

        三条路由：
          · 列表 + 布置 + 录音；
          · 播放器（`:sessionId`）；
          · 只读回看（`:sessionId/review`）。
      */}
      <Route
        path={routePath('/students/:id/dictation')}
        element={
          <RequireAuth role="parent">
            <ParentStudentDictationPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id/dictation/:sessionId')}
        element={
          <RequireAuth role="parent">
            <ParentDictationSessionPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/students/:id/dictation/:sessionId/review')}
        element={
          <RequireAuth role="parent">
            <ParentDictationReviewPage />
          </RequireAuth>
        }
      />
      {/*
        预习卡（田字格 + 拼音 + 笔顺动画）：听写**之前**看的一页。
        入口在播放器那一页顶部的「先预习一下」，上一级也是那里（见 `backNav.ts`）。
      */}
      <Route
        path={routePath('/students/:id/dictation/:sessionId/preview')}
        element={
          <RequireAuth role="parent">
            <ParentDictationPreviewPage />
          </RequireAuth>
        }
      />
      {/*
        错词本（第四步）。⚠️ 与上面那条 `:sessionId` **段数相同**（都是 5 段），
        区别只在第 4 段是静态的 `mistakes` —— react-router 静态优先，
        所以 `/students/x/dictation/mistakes` 命中这一条；Fastify 同一条规则
        （`dictation.routes-order.test.ts` 验了后端那一侧）。
        数据是**这个孩子的**（`dictation_mistake` 有 `student_id`）。
      */}
      <Route
        path={routePath('/students/:id/dictation/mistakes')}
        element={
          <RequireAuth role="parent">
            <ParentDictationMistakesPage />
          </RequireAuth>
        }
      />
      {/* 全局奖品入口：从底部导航进来时没有 :id，页面会先让孩子列表里选一个 */}
      <Route
        path={routePath('/rewards')}
        element={
          <RequireAuth role="parent">
            <ParentRewardsPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/settings')}
        element={
          <RequireAuth role="parent">
            <SettingsPage />
          </RequireAuth>
        }
      />

      {/* ---------- 学生端 ---------- */}
      <Route
        path={routePath('/student')}
        element={
          <RequireAuth>
            <StudentTodayPage />
          </RequireAuth>
        }
      />
      {/* 学校作业（学生端）：作业**不**出现在 `/student`（后端 `/student/today`
          无条件排除 `kind='homework'`），所以它必须有自己的一页与导航入口。 */}
      <Route
        path={routePath('/student/homework')}
        element={
          <RequireAuth>
            <StudentHomeworkPage />
          </RequireAuth>
        }
      />
      {/*
        语文听写（孩子端，迁移 0020 的批量任务）。
        · `/student/dictation`                    —— 家长布置的任务 + 「开始听写」；
        · `/student/dictation/:sessionId`         —— 这一批的播放器（含「提交这一批」）；
        · `/student/dictation/:sessionId/preview` —— **先预习一下**（田字格 + 拼音 + 笔顺）。
        入口在孩子端「今日打卡」页的听写卡片（`StudentDictationEntry`），
        以及播放器右上角的「先预习一下」（与家长端同一个位置）。
      */}
      <Route
        path={routePath('/student/dictation')}
        element={
          <RequireAuth>
            <StudentDictationPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/student/dictation/:sessionId')}
        element={
          <RequireAuth>
            <StudentDictationSessionPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/student/dictation/:sessionId/preview')}
        element={
          <RequireAuth>
            <StudentDictationPreviewPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/student/points')}
        element={
          <RequireAuth>
            <StudentPointsPage />
          </RequireAuth>
        }
      />
      <Route
        path={routePath('/student/rewards')}
        element={
          <RequireAuth>
            <StudentRewardsPage />
          </RequireAuth>
        }
      />
      {/* 学生改自己的密码（与家长端 /settings 共用同一页面，用 kind 区分） */}
      <Route
        path={routePath('/student/settings')}
        element={
          <RequireAuth>
            <SettingsPage kind="student" />
          </RequireAuth>
        }
      />

      {/* ---------- 系统管理后台（见方案 2B）----------
          入口刻意**不放在**家长/学生端的任何导航里，也不套 RequireGuest：
          管理员登录页自己处理"已登录"的情况。
          ⚠️ 设计上公网访问 /admin 应由 Caddy 拦掉（只允许内网 / SSH 隧道来源），
             但 2026-10-16 实测线上并未生效（caddy 跑的是旧配置），
             所以**不要**把"Caddy 会拦"当成前端的安全前提。
             详见 docs/RUNBOOK.md §5.4。 */}
      <Route path={routePath('/admin/login')} element={<AdminLoginPage />} />
      <Route
        path={routePath('/admin')}
        element={
          <RequireSystemAdmin>
            <AdminOverviewPage />
          </RequireSystemAdmin>
        }
      />
      <Route
        path={routePath('/admin/users')}
        element={
          <RequireSystemAdmin>
            <AdminUsersPage />
          </RequireSystemAdmin>
        }
      />
      {/*
        「听写字表」：字表维护的**唯一入口**（用户要求"放到系统管理员那里去"）。
        与家长端 `/tools/dictation` 共用同一个 `DictationWorkspace`，
        只换了 mode（maintain）与 scope（admin）—— 面板一行都没重写。
        ⚠️ 后端 `/admin/dictation/*` 另有 `requireSystemAdmin` 门禁 + Caddy 的
           内网限制（**该 Caddy 限制线上尚未生效**，见 docs/RUNBOOK.md §5.4）；
           这里的 `RequireSystemAdmin` 只是"别让人走到满屏 403 的页面"。
      */}
      <Route
        path={routePath('/admin/dictation')}
        element={
          <RequireSystemAdmin>
            <AdminDictationPage />
          </RequireSystemAdmin>
        }
      />
      <Route
        path={routePath('/admin/settings')}
        element={
          <RequireSystemAdmin>
            <AdminSettingsPage />
          </RequireSystemAdmin>
        }
      />
      <Route
        path={routePath('/admin/audit')}
        element={
          <RequireSystemAdmin>
            <AdminAuditPage />
          </RequireSystemAdmin>
        }
      />
      <Route
        path={routePath('/admin/mail')}
        element={
          <RequireSystemAdmin>
            <AdminMailTestPage />
          </RequireSystemAdmin>
        }
      />

      <Route path={routePath('/404')} element={<NotFoundPage />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
}
